import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
        console.dir(this);
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
    this.dispatchEvent(new CustomEvent('filters:updated', { bubbles: true, composed: true }));
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);


    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
// class PriceFacetComponent extends Component {
//   connectedCallback() {
//     super.connectedCallback();
//     this.addEventListener('keydown', this.#onKeyDown);
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     this.removeEventListener('keydown', this.#onKeyDown);
//   }

//   /**
//    * Handles keydown events to restrict input to valid characters
//    * @param {KeyboardEvent} event - The keydown event
//    */
//   #onKeyDown = (event) => {
//     if (event.metaKey) return;

//     const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
//     if (!event.key.match(pattern)) event.preventDefault();
//   };

//   /**
//    * Updates price filter and results
//    */
//   updatePriceFilterAndResults() {
//     const { minInput, maxInput } = this.refs;

//     this.#adjustToValidValues(minInput);
//     this.#adjustToValidValues(maxInput);
//     // console.dir(maxInput);
    

//     const facetsForm = this.closest('facets-form-component');
//     if (!(facetsForm instanceof FacetsFormComponent)) return;

//     facetsForm.updateFilters();
//     this.#setMinAndMaxValues();
//     this.#updateSummary();
//   }

//   /**
//    * Adjusts input values to be within valid range
//    * @param {HTMLInputElement} input - The input element to adjust
//    */
//   #adjustToValidValues(input) {
//     if (input.value.trim() === '') return;

//     // const value = Number(input.value);
//     const value = parseInt(input.value.replace(/,/g,'')) || 0;
//     // const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
//     // const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));
//     const min = parseInt((input.getAttribute('data-min') || '0').replace(/,/g,'')) || 0;
//     const max = parseInt((input.getAttribute('data-max') || '0').replace(/,/g,'')) || 0;
    

//     if (value < min) input.value = min;
//     if (value > max) input.value = max;

//   }

//   /**
//    * Sets min and max values for the inputs
//    */
//   #setMinAndMaxValues() {
//     const { minInput, maxInput } = this.refs;
//         console.dir(maxInput.value);

//     if (maxInput.value) minInput.setAttribute('data-max', maxInput.value.replace(/,/g,''));
//     if (minInput.value) maxInput.setAttribute('data-min', minInput.value.replace(/,/g,''));
//     if (minInput.value === '') maxInput.setAttribute('data-min', '0');
//     if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
//   }

//   /**
//    * Updates the price summary
//    */
//   #updateSummary() {
//     const { minInput, maxInput } = this.refs;
//     const details = this.closest('details');
//     const statusComponent = details?.querySelector('facet-status-component');

//     if (!(statusComponent instanceof FacetStatusComponent)) return;

//     statusComponent?.updatePriceSummary(minInput, maxInput);
//   }
// }

// if (!customElements.get('price-facet-component')) {
//   customElements.define('price-facet-component', PriceFacetComponent);
// }

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};












class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.minInput = this.querySelector('[ref="minInput"]');
    this.maxInput = this.querySelector('[ref="maxInput"]');

    if (!this.minInput || !this.maxInput) {
      console.warn('PriceFacetComponent: minInput or maxInput not found');
      return;
    }

    // keydown 制限は金額 input のみにバインド
    this.minInput.addEventListener('keydown', this.#onKeyDown);
    this.maxInput.addEventListener('keydown', this.#onKeyDown);

    // 初期表示方針:
    // - input 要素に既に値がある（属性 value または DOM value）か parentFacet に currentMin/currentMax がある場合はそれを使う
    // - そうでなければテキスト入力は空（ユーザーの要求通り）
    const parentFacet = this.closest('price-facet-component');

    const minHasPrefill =
      (this.minInput.getAttribute('value') || '').trim() !== '' ||
      (this.minInput.value || '').toString().trim() !== '' ||
      (parentFacet && typeof parentFacet.currentMin !== 'undefined');

    const maxHasPrefill =
      (this.maxInput.getAttribute('value') || '').trim() !== '' ||
      (this.maxInput.value || '').toString().trim() !== '' ||
      (parentFacet && typeof parentFacet.currentMax !== 'undefined');

    if (minHasPrefill) {
      // 属性値または現状の value、もしくは parentFacet の値を優先してセット
      this.minInput.value = this.#parseValue(
        (parentFacet && typeof parentFacet.currentMin !== 'undefined')
          ? String(parentFacet.currentMin)
          : (this.minInput.value || this.minInput.getAttribute('value') || this.minInput.getAttribute('data-min'))
      );
    } else {
      // 初回表示は空にする
      this.minInput.value = '';
    }

    if (maxHasPrefill) {
      this.maxInput.value = this.#parseValue(
        (parentFacet && typeof parentFacet.currentMax !== 'undefined')
          ? String(parentFacet.currentMax)
          : (this.maxInput.value || this.maxInput.getAttribute('value') || this.maxInput.getAttribute('data-max'))
      );
    } else {
      this.maxInput.value = '';
    }

    // inputイベントは金額 input のみ（外部 change による誤動作を防ぐため、updatePriceFilterAndResults は内部発火チェックあり）
    this.minInput.addEventListener('input', () => this.updatePriceFilterAndResults());
    this.maxInput.addEventListener('input', () => this.updatePriceFilterAndResults());
    
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.minInput?.removeEventListener('keydown', this.#onKeyDown);
    this.maxInput?.removeEventListener('keydown', this.#onKeyDown);
  }

  // 入力制限
  #onKeyDown = (e) => {
    if (e.metaKey) return;
    const allowed = /[0-9]|\.|,| |Backspace|Tab|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!e.key.match(allowed)) e.preventDefault();
  };

  /**
   * updatePriceFilterAndResults
   * - externalEvent が来た場合は発生元がこのコンポーネント内部かどうかをチェックして、
   *   外部発火なら何もしない（他の facet による巻き込みを防ぐ）
   */
  updatePriceFilterAndResults(externalEvent) {
    if (externalEvent instanceof Event) {
      const path = typeof externalEvent.composedPath === 'function' ? externalEvent.composedPath() : [externalEvent.target];
      const origin = path?.[0];
      if (origin && !this.#isEventOriginInside(origin)) {
        return; // 外部イベント -> 無視
      }
    }

    this.#adjustInput(this.minInput, this.maxInput);
    this.#adjustInput(this.maxInput, this.minInput);

    const facetsForm = this.closest('facets-form-component');
    if (facetsForm) facetsForm.updateFilters();

    this.#updateSummary();
  }

  // イベント発生元がこのコンポーネント内か（シャドウ含む）判定
  #isEventOriginInside(node) {
    let n = node;
    while (n) {
      if (n === this) return true;
      if (n instanceof ShadowRoot) {
        n = n.host;
        continue;
      }
      n = n.parentNode || (n.getRootNode && n.getRootNode().host) || null;
    }
    return false;
  }

  #adjustInput(input, other) {
    let value = this.#parseValue(input.value);
    let min = this.#parseValue(input.getAttribute('data-min') || '0');
    let max = this.#parseValue(input.getAttribute('data-max') || '0');

    if (other) {
      if (input === this.minInput && other.value) max = this.#parseValue(other.value) - 1;
      if (input === this.maxInput && other.value) min = this.#parseValue(other.value) + 1;
    }

    value = Math.max(min, Math.min(max, value));
    // 注意: input.value が空の可能性がある（初回表示） -> 空は維持する
    if (String(input.value).trim() === '') {
      // do nothing, keep empty
    } else {
      input.value = value;
    }
  }

  #updateSummary() {
    const details = this.closest('details');
    const status = details?.querySelector('facet-status-component');
    if (status) status.updatePriceSummary(this.minInput, this.maxInput);
  }

  #parseValue(str) {
    return parseInt((str || '0').replace(/,/g,''), 10) || 0;
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}



class PriceRange extends Component {
  constructor() {
    super();
    this.abortController = new AbortController();
  }

  connectedCallback() {
    super.connectedCallback();

    this.rangeMin = this.querySelector('input[type="range"][aria-label="から"]');
    this.rangeMax = this.querySelector('input[type="range"][aria-label="まで"]');
    this.inputMin = this.querySelector('[ref="minInput"]');
    this.inputMax = this.querySelector('[ref="maxInput"]');
    this.rangeGroup = this.querySelector('.range-group');

    if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax || !this.rangeGroup) {
      console.warn('<price-range> 必須項目 が見つかりません');
      return;
    }

    const parentFacet = this.closest('price-facet-component');

    // レンジのデフォルト（data-*）と親の current を取得
    const rangeMinDefault = this.#parseValue(this.inputMin.getAttribute('data-min'));
    const rangeMaxDefault = this.#parseValue(this.inputMax.getAttribute('data-max'));
    const parentMin = parentFacet?.currentMin;
    const parentMax = parentFacet?.currentMax;

    // input[type="text"] が既に値を持っているかを判定（空なら初回表示状態）
    const inputMinHasValue = (this.inputMin.value || '').toString().trim() !== '';
    const inputMaxHasValue = (this.inputMax.value || '').toString().trim() !== '';

    // range 属性（min/max）は data-* を使う
    this.rangeMin.min = rangeMinDefault;
    this.rangeMin.max = rangeMaxDefault;
    this.rangeMax.min = rangeMinDefault;
    this.rangeMax.max = rangeMaxDefault;

    // range の初期値は以下優先順：
    // 1. parentFacet.currentMin/currentMax があればそれ
    // 2. input[type="text"] に既に値があればそれ
    // 3. data-* のデフォルト
    const initialRangeMin = typeof parentMin !== 'undefined' ? parentMin : (inputMinHasValue ? this.#parseValue(this.inputMin.value) : rangeMinDefault);
    const initialRangeMax = typeof parentMax !== 'undefined' ? parentMax : (inputMaxHasValue ? this.#parseValue(this.inputMax.value) : rangeMaxDefault);

    this.rangeMin.value = initialRangeMin;
    this.rangeMax.value = initialRangeMax;

    // テキスト input が空の場合はそのまま空のままにする（ユーザーの指定）
    // ただし再描画や再読み込みで input に値が入っている場合は既に設定済みなので何もしない

    const updateStyle = () => {
      const minPercent = (parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max)) * 100;
      const maxPercent = (parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max)) * 100;
      this.rangeGroup.style.setProperty('--range-min', `${minPercent}%`);
      this.rangeGroup.style.setProperty('--range-max', `${maxPercent}%`);
    };

    const dispatchUpdateEvent = () => {
      if (!parentFacet) return;
      parentFacet.currentMin = this.#parseValue(this.inputMin.value || this.rangeMin.value);
      parentFacet.currentMax = this.#parseValue(this.inputMax.value || this.rangeMax.value);
      parentFacet.dispatchEvent(new CustomEvent('filters:updated', { bubbles: true }));
    };

    // sync 関数: number(text) → range
    const syncMin = () => {
      // テキストが空の場合は range に基づく（空のまま）
      if ((this.inputMin.value || '').toString().trim() === '') {
        // keep empty text, but ensure range is clamped
        let rv = this.#parseValue(this.rangeMin.value);
        rv = Math.min(rv, this.#parseValue(this.inputMax.value || this.rangeMax.value) - 1);
        this.rangeMin.value = rv;
      } else {
        let val = this.#parseValue(this.inputMin.value);
        val = Math.min(val, this.#parseValue(this.inputMax.value || this.rangeMax.value) - 1);
        this.inputMin.value = val;
        this.rangeMin.value = val;
      }
      updateStyle();
      dispatchUpdateEvent();
    };

    const syncMax = () => {
      if ((this.inputMax.value || '').toString().trim() === '') {
        let rv = this.#parseValue(this.rangeMax.value);
        rv = Math.max(rv, this.#parseValue(this.inputMin.value || this.rangeMin.value) + 1);
        this.rangeMax.value = rv;
      } else {
        let val = this.#parseValue(this.inputMax.value);
        val = Math.max(val, this.#parseValue(this.inputMin.value || this.rangeMin.value) + 1);
        this.inputMax.value = val;
        this.rangeMax.value = val;
      }
      updateStyle();
      dispatchUpdateEvent();
    };

    // input → range 同期
    this.inputMin.addEventListener('input', syncMin, { signal: this.abortController.signal });
    this.inputMax.addEventListener('input', syncMax, { signal: this.abortController.signal });

    // range → number 同期（ユーザーがスライダーを操作したら text に反映）
    this.rangeMin.addEventListener('input', () => {
      // スライダー操作が来たら text が空でもスライダー優先で値を反映（ユーザー操作時のみ）
      this.inputMin.value = this.rangeMin.value;
      syncMin();
    }, { signal: this.abortController.signal });

    this.rangeMax.addEventListener('input', () => {
      this.inputMax.value = this.rangeMax.value;
      syncMax();
    }, { signal: this.abortController.signal });

    // parentFacet が filters:updated を投げたときのリフレッシュ（既存挙動を維持）
    if (parentFacet) {
      const refreshRange = () => {
        this.inputMin.value = typeof parentFacet.currentMin !== 'undefined' ? String(parentFacet.currentMin) : this.inputMin.getAttribute('data-min') || '';
        this.inputMax.value = typeof parentFacet.currentMax !== 'undefined' ? String(parentFacet.currentMax) : this.inputMax.getAttribute('data-max') || '';

        this.rangeMin.value = this.inputMin.value !== '' ? this.#parseValue(this.inputMin.value) : this.rangeMin.getAttribute('min') || rangeMinDefault;
        this.rangeMax.value = this.inputMax.value !== '' ? this.#parseValue(this.inputMax.value) : this.rangeMax.getAttribute('max') || rangeMaxDefault;

        const minPercent = (parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max)) * 100;
        const maxPercent = (parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max)) * 100;

        this.rangeGroup.style.setProperty('--range-min', `${minPercent}%`);
        this.rangeGroup.style.setProperty('--range-max', `${maxPercent}%`);
      };
      parentFacet.addEventListener('filters:updated', refreshRange, { signal: this.abortController.signal });
    }

    updateStyle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abortController.abort();
  }

  #parseValue(str) {
    return parseInt((str || '0').replace(/,/g, ''), 10) || 0;
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

